import Cookies from 'js-cookie';

/**
 * SSE client for streaming AI chat responses via POST requests.
 * Uses fetch + ReadableStream since EventSource only supports GET.
 */
export default class SseClient {
    constructor() {
        this.abortController = null;
    }

    /**
     * Send a message and stream the response.
     *
     * @param {string} url
     * @param {Object} body
     * @param {Object} callbacks
     * @param {Function} callbacks.onToken - Called with each text chunk
     * @param {Function} callbacks.onDone - Called when stream completes with full text
     * @param {Function} callbacks.onError - Called on error
     * @param {Function} [callbacks.onEvent] - Called with any non-text parsed event
     */
    async stream(url, body, {
        onToken, onDone, onError, onEvent,
    }) {
        this.abort();

        this.abortController = new AbortController();
        let fullText = '';

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'text/event-stream',
                    'X-XSRF-TOKEN': decodeURIComponent(Cookies.get('XSRF-TOKEN') || ''),
                },
                credentials: 'same-origin',
                body: JSON.stringify(body),
                signal: this.abortController.signal,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Request failed with status ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let streamDone = false;
            let doneReceived = false;

            const processLine = (line) => {
                if (!line.startsWith('data: ')) return null;

                const data = line.slice(6);

                if (data === '[DONE]') {
                    doneReceived = true;
                    if (onDone) onDone(fullText);
                    return 'stop';
                }

                try {
                    const parsed = JSON.parse(data);

                    if (parsed.type === 'text_delta' && parsed.text) {
                        fullText += parsed.text;
                        if (onToken) onToken(parsed.text, fullText);
                    } else if (parsed.type === 'error') {
                        if (onError) onError(new Error(parsed.message || 'Stream error'));
                        doneReceived = true;
                        return 'stop';
                    } else if (onEvent) {
                        onEvent(parsed);
                    }
                } catch (parseError) {
                    // Ignore JSON parse errors — partial or malformed SSE lines
                }

                return null;
            };

            const readChunk = async () => { // eslint-disable-line no-async-promise-executor
                const { done, value } = await reader.read();

                if (done) {
                    streamDone = true;
                    return;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop();

                let i = 0;
                while (i < lines.length) {
                    const result = processLine(lines[i]);
                    if (result === 'stop') {
                        streamDone = true;
                        return;
                    }
                    i += 1;
                }
            };

            while (!streamDone) {
                await readChunk(); // eslint-disable-line no-await-in-loop
            }

            if (!doneReceived) {
                if (fullText) {
                    // Stream dropped mid-response — save what we have but flag it
                    if (onDone) onDone(fullText, { incomplete: true });
                } else if (onError) {
                    onError(new Error('Connection lost. Please try again.'));
                }
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                if (onDone) onDone(fullText, { incomplete: !!fullText });
                return;
            }

            if (onError) {
                onError(error);
            }
        } finally {
            this.abortController = null;
        }
    }

    abort() {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    }
}
